import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getGridFilterValues } from "../../../services/dataGridService";
import _ from "lodash";

import { SFGridItem } from "../../grid";
import { FormControl, FormLabel } from "@mui/material";
import getStyled from "../../../hooks/getStyled";

import RadioRenderer from "./renderers/radioRenderer";
import CheckboxRenderer from "./renderers/checkboxRenderer";
import SelectRenderer from "./renderers/selectRenderer";
import SliderRenderer from "./renderers/sliderRenderer";
import DateRenderer from "./renderers/dateRenderer";
import SFDivider from "../../divider";
import { FILTER_TYPES, GRID_FILTERS } from "../dataGridConfig";
import { resolveDateFilterModel } from "../index";
import TooltipButton from "../tooltipButton";

const { styled } = getStyled();

const FormControlStyled = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== "hierarchyIndex",
})(({ theme, hierarchyIndex }) => ({
  display: "flex",
  "& .MuiFormLabel-root": {
    ...theme.typography.tableList,
  },
  ...(hierarchyIndex && {
    marginLeft: theme.spacing(hierarchyIndex * 2.5),
  }),
}));

const SFTooltipContainerStyled = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
}));

const FilterTemplate = (props) => {
  const [config, setConfig] = useState({
    loading: true,
    values: [],
    displayValues: [],
  });

  const colId = props.colDef.field;
  const generatedFilter = props.getGeneratedFilter(colId);

  // Resolve the filter type that will be sent to the api
  // based on the custom basic filtering type
  const filterType = useMemo(() => {
    switch (props.type) {
    case FILTER_TYPES.DATE:
      return GRID_FILTERS.filterType.date;
    case FILTER_TYPES.NUMBER:
      return GRID_FILTERS.filterType.number;
    case FILTER_TYPES.DROPDOWN:
    case FILTER_TYPES.HIERARCHY:
      return GRID_FILTERS.filterType.set;
    default:
      return props.type;
    }
  }, []);

  // The getValuesFilter function calculates the filter built in the panel
  // disregarding the current field. Whenever it's value changes we make
  // an api request to retrieve the new values
  const getValues = useCallback(() => {
    if (props.colDef.filterParams?.hasFixedFilterValues) {
      return Promise.resolve({
        data: {
          values: props.colDef.filterParams?.values,
          displayValues:
            props.colDef.filterParams?.displayValues ??
            props.colDef.filterParams?.values,
        },
      });
    }
    const filterModel = _.cloneDeep(props.getValuesFilter(colId));
    resolveDateFilterModel(filterModel);
    return getGridFilterValues(
      props.filterValuesUrl,
      JSON.stringify({
        colName: colId,
        filterType,
        filterModel,
      })
    );
  }, [JSON.stringify(props.getValuesFilter(colId))]);

  useEffect(() => {
    setConfig((prv) => ({ ...prv, loading: true }));
    // If the filter type is a date then we do not need to call getValues function
    if (filterType === GRID_FILTERS.filterType.date) return props.onLoad(colId);

    getValues().then((res) => {
      // This is for the first render in order to know when the values
      // for the field are ready to display.
      props.onLoad(colId);
      //we need the request to trigger either way to supply ag grid filter with the values.

      let resultValues = res.data;
      if (props.colDef.filterParams?.fixedFilterValues) {
        resultValues.values =
          props.colDef.filterParams?.fixedFilterValues.values;
        resultValues.displayValues =
          props.colDef.filterParams?.fixedFilterValues.displayValues ??
          props.colDef.filterParams?.fixedFilterValues.values;
      }
      setConfig((prv) => ({ ...prv, ...res.data, loading: false }));

      // If the filter is the NUMBER type, since the behaviour is different, we check it inside the renderer
      if (props.colDef.filter !== FILTER_TYPES.NUMBER) {
        // When the previous selected values have a value that is not
        // included in the new values from the request we remove that
        // values from the selected list
        const previousValues = props.getGeneratedFilter(colId);
        if (previousValues && previousValues.length > 0) {
          const updatedValues = previousValues.filter((x) =>
            res.data.values.some((y) => y === x || y?.toString() === x)
          );
          if (updatedValues.length !== previousValues.length) {
            onChange(updatedValues);
          }
        }
      }
    });
  }, [getValues]);

  const onChange = (e) => {
    if (!e || e.length === 0) {
      props.clearFilter(colId);
    } else {
      props.onChange(colId, e);
    }
  };

  if (props.loading || !config) return null;

  const componentProps = {
    config,
    generatedFilter,
    onChange,
    colId,
    colDef: props.colDef,
    filterValuesUrl: props.filterValuesUrl,
    getValuesFilter: () => props.getValuesFilter(colId),
    hierarchyIndex: props.hierarchyIndex,
    disabled: props.colDef.showBasicFilterDisabled ?? props.colDef.context?.showBasicFilterDisabled,
  };

  let Component;
  switch (props.type) {
  case FILTER_TYPES.BOOLEAN:
  case FILTER_TYPES.RADIO:
    Component = RadioRenderer;
    break;
  case FILTER_TYPES.CATEGORY:
    Component = CheckboxRenderer;
    break;
  case FILTER_TYPES.NUMBER:
    Component = SliderRenderer;
    break;
  case FILTER_TYPES.DATE:
    Component = DateRenderer;
    break;
  case FILTER_TYPES.DROPDOWN:
  default:
    Component = SelectRenderer;
  }

  // Get filterTooltip from either colDef or colDef.context (for AG Grid v33 compatibility)
  const filterTooltip =
    props.colDef.filterTooltip ?? props.colDef.context?.filterTooltip;

  return (
    <>
      <SFGridItem xs={12}>
        <FormControlStyled hierarchyIndex={props.hierarchyIndex}>
          {filterTooltip ? (
            <SFTooltipContainerStyled className="filter-tooltip-container">
              <FormLabel>
                {props.colDef.filterParams?.customLabel ??
                  props.colDef.headerName}
              </FormLabel>
              <TooltipButton
                position="right"
                tooltip={filterTooltip}
                iconColor="grey500"
                width="narrow"
              />
            </SFTooltipContainerStyled>
          ) : (
            <FormLabel>
              {props.colDef.filterParams?.customLabel ??
                props.colDef.headerName}
            </FormLabel>
          )}
          <Component {...componentProps} />
        </FormControlStyled>
      </SFGridItem>
      {!props.loading && props.hasDivider && (
        <SFGridItem xs={12}>
          <SFDivider variant="dashed" />
        </SFGridItem>
      )}
    </>
  );
};

FilterTemplate.defaultProps = {
  hierarchyIndex: null,
};

FilterTemplate.propTypes = {
  // The type that decides which renderer to use
  type: PropTypes.oneOf(_.values(FILTER_TYPES)).isRequired,
  // The column definition of the field
  colDef: PropTypes.object.isRequired,
  // Whether the field has a divider after it
  hasDivider: PropTypes.bool.isRequired,
  // A function that gets the current filter for this field
  getGeneratedFilter: PropTypes.func.isRequired,
  // A function that applies the changes to the generated filter model
  onChange: PropTypes.func.isRequired,
  // A function that clears the generated filter model for this field
  clearFilter: PropTypes.func.isRequired,
  // The ag grid's api
  gridApi: PropTypes.object.isRequired,
  // The api endpoint that returns the values for the fields
  filterValuesUrl: PropTypes.string.isRequired,
  // A function that gets the current filter disregarding this field
  getValuesFilter: PropTypes.func.isRequired,
  // Whether the component is loading
  loading: PropTypes.bool.isRequired,
  // A function used to tell the panel that this field has loaded
  // Used for the first render in order to show the loading spinner
  onLoad: PropTypes.func.isRequired,
  // The index the field inside the hierarchy component
  hierarchyIndex: PropTypes.number,
};

export default FilterTemplate;
